/**
 * RealtimeClient — Browser-Side WS-Client für die OpenAI Realtime API
 * (proxied über unseren /realtime-Endpoint).
 *
 * Architektur:
 *   getUserMedia({echoCancellation, noiseSuppression, autoGainControl})
 *     → AudioWorklet (downsample 48kHz → 24kHz PCM16)
 *     → WebSocket(/realtime?token=…)
 *     ⇄ OpenAI Realtime
 *     → input_audio_buffer.append / response.audio.delta
 *     → Output: AudioBufferQueue (statt <audio>-Element → umgeht iOS-Autoplay-Block)
 *
 * iOS-Audio-Unlock:
 *   AudioContext wird im SAME-TICK des User-Taps erzeugt + resume()'d.
 *   AudioContext gilt damit für die ganze Session als "unlocked".
 *
 * Server-VAD ist aktiv (im Backend konfiguriert) → Barge-in / Auto-Turns
 * werden vom Server erkannt. Wir hören auf "input_audio_buffer.speech_started"
 * und unterbrechen die Wiedergabe für die UI (visueller "barge-in"-Effekt).
 */

const SAMPLE_RATE_OUT = 24_000;
const SAMPLE_RATE_IN_NATIVE = 48_000; // typisch in modernen Browsern
const FRAME_SIZE_MS = 20; // 20 ms PCM-Chunks an den Server

export type RealtimeState = "idle" | "connecting" | "ready" | "listening" | "thinking" | "speaking" | "closed" | "error";

export interface RealtimeHandlers {
  onState(state: RealtimeState): void;
  /** User fängt an zu sprechen — UI sollte sofort Placeholder anlegen,
   * damit die User-Bubble VOR der Assistant-Antwort erscheint. */
  onUserSpeechStarted?(): void;
  onUserTranscript(text: string, final: boolean): void;
  onAssistantTranscript(text: string, final: boolean): void;
  onAssistantAudioLevel(rms: number): void;
  onMicLevel(rms: number): void;
  onToolStarted?(name: string, args: unknown): void;
  onToolDone?(name: string, ok: boolean, summary: string): void;
  onError(message: string): void;
  onHallucinationFiltered?(original: string): void;
}

export interface RealtimeOptions {
  token: string;
  voice: string;
  handlers: RealtimeHandlers;
  /** WS-URL Override für Tests. Default: same-origin /realtime. */
  url?: string;
  /** Speaker-Mode: Half-Duplex erzwingen — Mic-Frames während Assistant-Audio
   *  werden NICHT mehr an den Server geschickt. Verhindert Akustik-Loop wenn
   *  Selman über iPhone-Lautsprecher redet. Default false (AirPods/Headset). */
  speakerMode?: boolean;
}

interface AudioBufferQueueItem {
  buffer: AudioBuffer;
  source: AudioBufferSourceNode;
}

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private playbackHead = 0;
  private playbackQueue: AudioBufferQueueItem[] = [];
  private playbackGain: GainNode | null = null;
  private playbackAnalyser: AnalyserNode | null = null;
  private analyserBuf: Float32Array | null = null;
  private micAnalyser: AnalyserNode | null = null;
  private micAnalyserBuf: Float32Array | null = null;
  private rafId = 0;
  private opts: RealtimeOptions;
  private state: RealtimeState = "idle";
  private userTranscriptAggregate = "";
  /** Laufende User-STT-Deltas pro Item-ID (Tail-Recovery für long speech). */
  private userTranscriptByItem = new Map<string, string>();
  private assistantTranscriptAggregate = "";
  private closedByUser = false;
  /**
   * Hybrid-Barge-In: solange wir Audio abspielen, messen wir parallel das
   * Mic-RMS-Level. Liegt es über `bargeInThreshold` für `bargeInMinFrames`
   * Frames in Folge, interpretieren wir das als "echter User redet jetzt"
   * und canceln die laufende Antwort sofort — wir warten NICHT auf Server-VAD.
   *
   * Die Schwelle ist bewusst hoch (0.15) — niedrige Werte werden vom
   * Browser-AEC (echoCancellation:true) i.d.R. unterdrückt, alles darüber ist
   * direkter Stimm-Schall ins Mic. Im AirPods-Mode greift das selten, weil das
   * Echo schon hardware-seitig fast 0 ist; im Speaker-Mode würde das Echo
   * tatsächlich Threshold überschreiten, daher dort weiter gated (siehe
   * speakerMode + micGated).
   */
  private bargeInThreshold = 0.15;
  // ~80ms bei 60fps RAF, ~160ms bei 30fps RAF (Mobile/Low-Power). Codex-Hinweis:
  // 3 Frames war zu kurz für 30Hz-Devices. 5 ist immer noch sub-200ms im
  // Worst-Case, schneller als Server-VAD.
  private bargeInMinFrames = 5;
  private bargeInFrameCounter = 0;
  /** Lock: nach einem barge-in-Interrupt warten wir bis "listening" zurück ist, bevor wir nochmal feuern. */
  private bargeInArmed = true;
  /**
   * Half-Duplex-Gate für Speaker-Mode: solange `true`, werden mic-PCM-Frames
   * NICHT an den Server weitergegeben. Wird gesetzt sobald JARVIS zu sprechen
   * beginnt, und mit 300ms Tail-Delay nach Audio-Ende wieder geöffnet.
   * Mic-Worklet capture läuft weiter (lokales RMS für Orb-Visualisierung).
   */
  private micGated = false;
  private micGateReleaseTimer: ReturnType<typeof setTimeout> | null = null;
  private speakerMode = false;
  /** Letzte vollständige Assistant-Sprach-Ausgabe — für Self-Echo-Duplicate-Check. */
  private lastAssistantFinalText = "";

  constructor(opts: RealtimeOptions) {
    this.opts = opts;
    this.speakerMode = !!opts.speakerMode;
  }

  getState(): RealtimeState {
    return this.state;
  }

  /**
   * Speaker-Mode zur Laufzeit umschalten — keine Reconnect-Notwendigkeit,
   * weil Half-Duplex rein client-seitig gesteuert ist.
   */
  setSpeakerMode(enabled: boolean): void {
    this.speakerMode = enabled;
    if (!enabled) {
      // Bei Wechsel zu Full-Duplex sofort Gate öffnen, falls noch gesetzt.
      this.openMicGate();
    }
  }

  isSpeakerMode(): boolean {
    return this.speakerMode;
  }

  /** Start: AudioContext erzeugen + entsperren, Mic öffnen, WS verbinden. */
  async start(): Promise<void> {
    if (this.state !== "idle" && this.state !== "closed" && this.state !== "error") return;
    this.closedByUser = false;
    this.setState("connecting");

    // 1. AudioContext SOFORT im User-Gesture-Tick erzeugen + resumen. Das ist
    //    der iOS-Unlock-Trick — Output via AudioContext (nicht <audio>-Element)
    //    umgeht die meisten Autoplay-Restriktionen.
    try {
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new Ctx({ sampleRate: SAMPLE_RATE_OUT, latencyHint: "interactive" });
      if (this.audioCtx.state === "suspended") {
        await this.audioCtx.resume();
      }
      // Output-Chain: Queue → GainNode → AnalyserNode → destination.
      this.playbackGain = this.audioCtx.createGain();
      this.playbackAnalyser = this.audioCtx.createAnalyser();
      this.playbackAnalyser.fftSize = 512;
      this.playbackAnalyser.smoothingTimeConstant = 0.4;
      this.playbackGain.connect(this.playbackAnalyser);
      this.playbackAnalyser.connect(this.audioCtx.destination);
      this.analyserBuf = new Float32Array(this.playbackAnalyser.fftSize);
      this.playbackHead = this.audioCtx.currentTime;
    } catch (err) {
      this.fail(`AudioContext: ${describe(err)}`);
      return;
    }

    // 2. Mic mit aggressivem Echo-Cancel öffnen (kritisch — Selman testet via
    //    iPhone-Lautsprecher, JARVIS-Stimme darf nicht zurück ins Mic leaken).
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });
    } catch (err) {
      this.fail(`Mic: ${describe(err)}`);
      return;
    }

    // 3. AudioWorklet laden (downsample + PCM16-Encode im Audio-Thread).
    try {
      const blob = new Blob([WORKLET_SRC], { type: "application/javascript" });
      const url = URL.createObjectURL(blob);
      await this.audioCtx!.audioWorklet.addModule(url);
      URL.revokeObjectURL(url);
    } catch (err) {
      this.fail(`AudioWorklet: ${describe(err)}`);
      return;
    }

    try {
      this.sourceNode = this.audioCtx!.createMediaStreamSource(this.mediaStream);
      this.workletNode = new AudioWorkletNode(this.audioCtx!, "veloro-pcm-encoder", {
        processorOptions: {
          targetSampleRate: SAMPLE_RATE_OUT,
          frameSizeMs: FRAME_SIZE_MS,
        },
      });
      this.workletNode.port.onmessage = (ev: MessageEvent) => {
        const data = ev.data;
        if (!data) return;
        if (data.type === "pcm" && data.payload instanceof ArrayBuffer) {
          this.sendPcm(data.payload as ArrayBuffer);
        } else if (data.type === "level" && typeof data.rms === "number") {
          this.opts.handlers.onMicLevel(data.rms);
        }
      };

      // Mic-Analyser für Backup-Level (falls Worklet kein RMS publisht)
      this.micAnalyser = this.audioCtx!.createAnalyser();
      this.micAnalyser.fftSize = 512;
      this.micAnalyser.smoothingTimeConstant = 0.5;
      this.micAnalyserBuf = new Float32Array(this.micAnalyser.fftSize);
      this.sourceNode.connect(this.micAnalyser);
      this.sourceNode.connect(this.workletNode);
      // WICHTIG: NICHT mit audioCtx.destination verbinden — sonst hört Selman
      // sein eigenes Mic durch den Lautsprecher (Feedback-Loop).
    } catch (err) {
      this.fail(`AudioGraph: ${describe(err)}`);
      return;
    }

    // 4. WebSocket öffnen.
    this.openWs();
  }

  /** Beende die Session sauber. */
  async stop(): Promise<void> {
    this.closedByUser = true;
    this.setState("closed");
    try {
      this.ws?.close(1000, "client stop");
    } catch {
      // ignore
    }
    this.ws = null;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
    try {
      this.workletNode?.port.close();
      this.workletNode?.disconnect();
    } catch {
      // ignore
    }
    this.workletNode = null;
    try {
      this.sourceNode?.disconnect();
    } catch {
      // ignore
    }
    this.sourceNode = null;
    if (this.mediaStream) {
      for (const track of this.mediaStream.getTracks()) track.stop();
      this.mediaStream = null;
    }
    try {
      await this.audioCtx?.close();
    } catch {
      // ignore
    }
    this.audioCtx = null;
    this.playbackGain = null;
    this.playbackAnalyser = null;
    this.playbackQueue = [];
    if (this.micGateReleaseTimer) {
      clearTimeout(this.micGateReleaseTimer);
      this.micGateReleaseTimer = null;
    }
    this.micGated = false;
  }

  /**
   * Mikrofon temporär stummschalten ohne die Session zu beenden.
   * Track bleibt aktiv (kein Re-Permission nötig), nur `enabled=false`.
   */
  setMuted(muted: boolean): void {
    if (!this.mediaStream) return;
    for (const track of this.mediaStream.getAudioTracks()) {
      track.enabled = !muted;
    }
  }

  isMuted(): boolean {
    if (!this.mediaStream) return false;
    const tracks = this.mediaStream.getAudioTracks();
    if (tracks.length === 0) return false;
    return tracks.every((t) => !t.enabled);
  }

  /** Sende einen Trigger, sodass das Modell jetzt antwortet (für PTT/Manual-Mode). */
  triggerResponse(): void {
    this.sendEvent({ type: "response.create" });
  }

  /**
   * Schickt einen User-Text direkt in die Realtime-Session (z. B. wenn Selman
   * tippt statt spricht). Legt ein conversation.item.create mit dem Text an
   * und triggert eine Antwort. Wenn JARVIS gerade spricht: erst Audio canceln,
   * damit der Text-Input wie ein Barge-In wirkt.
   *
   * @param textOnlyReply Wenn true → Modell bekommt explizit per-response
   *   `output_modalities: ["text"]`. JARVIS antwortet dann nur als Text (kein
   *   Audio-Stream). Default false → normale Audio-Antwort.
   */
  sendUserText(text: string, textOnlyReply = false): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    if (this.state === "speaking") {
      this.cancelResponse();
    }
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text: trimmed }],
      },
    });
    if (textOnlyReply) {
      // Per-response Override: GA-Realtime erlaubt output_modalities pro
      // response.create. Damit antwortet das Modell text-only ohne dass wir
      // die Session-Default-Modalität ändern müssen (Voice-Input bleibt voll
      // funktional in der gleichen Session).
      this.sendEvent({
        type: "response.create",
        response: { output_modalities: ["text"] },
      });
    } else {
      this.sendEvent({ type: "response.create" });
    }
  }

  /**
   * Schickt ein Bild (data URL) + Prompt direkt in die Realtime-Session.
   * Das Bild landet als `input_image` im Conversation-Context.
   */
  sendUserImage(imageDataUrl: string, prompt: string): void {
    const image = imageDataUrl.trim();
    const text = prompt.trim() || "Was siehst du auf diesem Bild?";
    if (!image) return;
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    if (this.state === "speaking") {
      this.cancelResponse();
    }
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [
          { type: "input_text", text },
          { type: "input_image", image_url: image },
        ],
      },
    });
    this.sendEvent({ type: "response.create" });
  }

  /** Hart die laufende JARVIS-Antwort canceln (barge-in / Stop-Button). */
  cancelResponse(): void {
    // Nur cancel senden wenn aktuell wirklich eine response läuft — sonst
    // wirft OpenAI "no active response found" als veloro.error zurück.
    // assistantSpeakingSince > 0 = JARVIS streamt grad Audio.
    if (this.assistantSpeakingSince > 0 || this.state === "thinking" || this.state === "speaking") {
      this.sendEvent({ type: "response.cancel" });
    }
    this.clearPlayback();
  }

  // ── Internal ──────────────────────────────────────────────────────────────

  private openWs(): void {
    const speakerParam = this.speakerMode ? "&speakerMode=1" : "";
    const url = this.opts.url
      ? `${this.opts.url}?token=${encodeURIComponent(this.opts.token)}&voice=${encodeURIComponent(this.opts.voice)}${speakerParam}`
      : this.sameOriginRealtimeUrl();
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      this.fail(`WebSocket: ${describe(err)}`);
      return;
    }
    this.ws = ws;
    ws.binaryType = "arraybuffer";
    ws.onopen = () => {
      // Server konfiguriert Session selbst sobald upstream offen ist.
      // Wir warten auf "veloro.ready".
    };
    ws.onmessage = (ev) => {
      let frame: string;
      if (typeof ev.data === "string") {
        frame = ev.data;
      } else if (ev.data instanceof ArrayBuffer) {
        frame = new TextDecoder().decode(ev.data);
      } else {
        return;
      }
      this.handleServerEvent(frame);
    };
    ws.onerror = () => {
      // onclose folgt; Fehler-Message dort
    };
    ws.onclose = (ev) => {
      if (this.closedByUser) return;
      const reason = ev.reason || `code ${ev.code}`;
      if (ev.code === 4401) {
        this.fail(`Auth: ${reason}`);
      } else if (this.state === "ready" || this.state === "listening" || this.state === "speaking" || this.state === "thinking") {
        this.setState("closed");
        this.opts.handlers.onError(`Verbindung getrennt (${reason})`);
      } else {
        this.fail(`WS-Close: ${reason}`);
      }
    };
    this.startLevelLoop();
  }

  private sameOriginRealtimeUrl(): string {
    const loc = window.location;
    const proto = loc.protocol === "https:" ? "wss:" : "ws:";
    const speakerParam = this.speakerMode ? "&speakerMode=1" : "";
    return `${proto}//${loc.host}/realtime?token=${encodeURIComponent(this.opts.token)}&voice=${encodeURIComponent(this.opts.voice)}${speakerParam}`;
  }

  private sendPcm(buf: ArrayBuffer): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    // Half-Duplex: solange JARVIS spricht (Speaker-Mode), Mic-Frames NICHT
    // weitergeben. Worklet läuft weiter (lokales Orb-RMS bleibt erhalten),
    // aber wir lassen das Audio nicht in Whisper/VAD ankommen.
    if (this.speakerMode && this.micGated) return;
    // OpenAI Realtime wartet base64-PCM16 in einem input_audio_buffer.append-Event.
    const b64 = arrayBufferToBase64(buf);
    this.ws.send(JSON.stringify({ type: "input_audio_buffer.append", audio: b64 }));
  }

  /** Half-Duplex: Gate schließen — Mic-Output an Server stoppt sofort. */
  private closeMicGate(): void {
    if (this.micGateReleaseTimer) {
      clearTimeout(this.micGateReleaseTimer);
      this.micGateReleaseTimer = null;
    }
    this.micGated = true;
  }

  /**
   * Half-Duplex: Gate mit 300ms Tail-Delay öffnen. Das Delay räumt das
   * Echo-Tail im Mic auf bevor wir wieder Frames durchlassen — andernfalls
   * triggert das Reverb-Tail sofort wieder VAD.
   */
  private scheduleMicGateRelease(): void {
    if (this.micGateReleaseTimer) {
      clearTimeout(this.micGateReleaseTimer);
    }
    this.micGateReleaseTimer = setTimeout(() => {
      this.micGated = false;
      this.micGateReleaseTimer = null;
    }, 300);
  }

  /** Sofort öffnen (z.B. bei Speaker-Mode-Toggle oder Stop). */
  private openMicGate(): void {
    if (this.micGateReleaseTimer) {
      clearTimeout(this.micGateReleaseTimer);
      this.micGateReleaseTimer = null;
    }
    this.micGated = false;
  }

  private sendEvent(payload: unknown): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(payload));
  }

  private handleServerEvent(frame: string): void {
    let ev: any;
    try {
      ev = JSON.parse(frame);
    } catch {
      return;
    }
    const type: string = ev?.type ?? "";

    if (type === "veloro.ready") {
      this.setState("listening");
      return;
    }
    if (type === "veloro.error") {
      this.opts.handlers.onError(String(ev.message ?? "unknown error"));
      return;
    }
    if (type === "veloro.upstream_closed") {
      this.opts.handlers.onError(`OpenAI getrennt: ${ev.reason || `code ${ev.code}`}`);
      this.setState("closed");
      return;
    }
    if (type === "veloro.tool_started") {
      this.opts.handlers.onToolStarted?.(String(ev.name), ev.args);
      return;
    }
    if (type === "veloro.tool_done") {
      this.opts.handlers.onToolDone?.(String(ev.name), !!ev.ok, String(ev.summary ?? ""));
      return;
    }
    if (type === "veloro.hallucination_filtered") {
      this.opts.handlers.onHallucinationFiltered?.(String(ev.original ?? ""));
      return;
    }

    // OpenAI-Events
    if (type === "input_audio_buffer.speech_started") {
      // Barge-In: laufende Wiedergabe stoppen, sofort wieder "listening" zeigen.
      this.clearPlayback();
      this.setState("listening");
      this.assistantTranscriptAggregate = "";
      // UI signal: User-Bubble JETZT als Placeholder anlegen, damit sie VOR
      // der Assistant-Antwort im Transkript erscheint (Whisper-Completion
      // kommt sonst erst nach dem Audio-Stream).
      this.opts.handlers.onUserSpeechStarted?.();
      return;
    }
    if (type === "input_audio_buffer.speech_stopped") {
      this.setState("thinking");
      return;
    }
    if (type === "conversation.item.input_audio_transcription.delta") {
      const delta = typeof ev.delta === "string" ? ev.delta : "";
      if (!delta) return;
      const itemId = typeof ev.item_id === "string" ? ev.item_id : "";
      if (itemId) {
        const next = (this.userTranscriptByItem.get(itemId) || "") + delta;
        this.userTranscriptByItem.set(itemId, next);
        this.opts.handlers.onUserTranscript(next, false);
      } else {
        this.userTranscriptAggregate += delta;
        this.opts.handlers.onUserTranscript(this.userTranscriptAggregate, false);
      }
      return;
    }
    if (type === "conversation.item.input_audio_transcription.completed") {
      const itemId = typeof ev.item_id === "string" ? ev.item_id : "";
      const fromDone = typeof ev.transcript === "string" ? ev.transcript : "";
      const fromDelta = itemId ? (this.userTranscriptByItem.get(itemId) || "") : "";
      const merged = mergeTranscriptSegments(fromDelta, fromDone).trim();
      if (itemId) this.userTranscriptByItem.delete(itemId);
      this.userTranscriptAggregate = merged;
      // Bug-Analyse:
      // Bei langen 30s+ Turns kam das Tail teils nur über delta-Events. Wir
      // hatten vorher ausschließlich `completed` verwendet und konnten dadurch
      // den späteren Teil verlieren, wenn `completed` kürzer/abgeschnitten war
      // oder Item-Splits passierten. Deshalb mergen wir delta + completed pro
      // item_id und geben das zusammengeführte Ergebnis ans UI.
      const text = merged;
      if (text) this.opts.handlers.onUserTranscript(text, true);
      // Server hat create_response: false. Wir triggern response.create manuell
      // NUR wenn Whisper echten Text >=3 Zeichen lieferte. Leerer Text /
      // Echo-Phantom / Tap-Geräusch → kein response. Egal welcher Modus.
      if (text && text.length >= 3) {
        this.sendEvent({ type: "response.create" });
      }
      return;
    }
    // Audio-Transcript: GA und Beta-Shape parallel unterstützen.
    if (type === "response.audio_transcript.delta" || type === "response.output_audio_transcript.delta") {
      const delta = typeof ev.delta === "string" ? ev.delta : "";
      if (!delta) return;
      this.assistantTranscriptAggregate += delta;
      this.opts.handlers.onAssistantTranscript(this.assistantTranscriptAggregate, false);
      return;
    }
    if (type === "response.audio_transcript.done" || type === "response.output_audio_transcript.done") {
      const text = typeof ev.transcript === "string"
        ? ev.transcript
        : this.assistantTranscriptAggregate;
      this.lastAssistantFinalText = text || "";
      this.opts.handlers.onAssistantTranscript(text, true);
      return;
    }
    // Text-only Responses (Auto-Mode → User tippt → JARVIS antwortet als Text).
    // GA: response.output_text.delta/done  · Beta: response.text.delta/done.
    if (type === "response.output_text.delta" || type === "response.text.delta") {
      const delta = typeof ev.delta === "string" ? ev.delta : "";
      if (!delta) return;
      this.assistantTranscriptAggregate += delta;
      // Text-Responses haben keinen Audio-Stream → State direkt auf "thinking"
      // halten, der Orb bleibt nicht im speaking-Modus hängen.
      if (this.state !== "thinking") this.setState("thinking");
      this.opts.handlers.onAssistantTranscript(this.assistantTranscriptAggregate, false);
      return;
    }
    if (type === "response.output_text.done" || type === "response.text.done") {
      const text = typeof ev.text === "string"
        ? ev.text
        : this.assistantTranscriptAggregate;
      this.lastAssistantFinalText = text || "";
      this.opts.handlers.onAssistantTranscript(text, true);
      return;
    }
    // Audio-Output: GA = response.output_audio.delta, Beta = response.audio.delta.
    if (type === "response.audio.delta" || type === "response.output_audio.delta") {
      const b64 = typeof ev.delta === "string" ? ev.delta : "";
      if (!b64) return;
      this.enqueueAudio(b64);
      if (this.state !== "speaking") this.setState("speaking");
      return;
    }
    if (type === "response.audio.done" || type === "response.output_audio.done") {
      // Nichts tun — Wiedergabe läuft aus der Queue aus.
      return;
    }
    if (type === "response.done") {
      // Wiedergabe-Queue kann noch nicht durch sein; State wechselt im audio-end.
      if (this.playbackQueue.length === 0 && this.state !== "listening") {
        this.setState("listening");
      }
      return;
    }
    if (type === "error") {
      const msg = ev.error?.message || ev.message || "OpenAI-Error";
      this.opts.handlers.onError(String(msg));
    }
  }

  private enqueueAudio(b64: string): void {
    if (!this.audioCtx || !this.playbackGain) return;
    const ctx = this.audioCtx;
    const pcm = base64ToInt16(b64);
    const float = new Float32Array(pcm.length);
    for (let i = 0; i < pcm.length; i++) float[i] = pcm[i] / 32768;
    const buffer = ctx.createBuffer(1, float.length, SAMPLE_RATE_OUT);
    buffer.copyToChannel(float, 0);
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(this.playbackGain);
    // Stitch frames contiguously.
    const startAt = Math.max(ctx.currentTime + 0.02, this.playbackHead);
    source.start(startAt);
    this.playbackHead = startAt + buffer.duration;
    const item: AudioBufferQueueItem = { buffer, source };
    this.playbackQueue.push(item);
    source.onended = () => {
      // Remove from queue (cheap — small queue).
      const idx = this.playbackQueue.indexOf(item);
      if (idx >= 0) this.playbackQueue.splice(idx, 1);
      if (this.playbackQueue.length === 0 && this.state === "speaking" && !this.closedByUser) {
        this.setState("listening");
      }
    };
  }

  /**
   * Barge-In: alle geplanten/laufenden Audio-Sources hart stoppen + Gain-Ramp,
   * damit's klickfrei wird. Reset von playbackHead, damit die nächste Antwort
   * direkt loslegt.
   */
  private clearPlayback(): void {
    if (!this.audioCtx) {
      this.playbackQueue = [];
      return;
    }
    const ctx = this.audioCtx;
    const now = ctx.currentTime;

    // 1. Click-frei runter-rampen.
    if (this.playbackGain) {
      try {
        this.playbackGain.gain.cancelScheduledValues(now);
        this.playbackGain.gain.setValueAtTime(this.playbackGain.gain.value, now);
        this.playbackGain.gain.linearRampToValueAtTime(0, now + 0.03);
      } catch {
        // ignore
      }
    }

    // 2. ALLE scheduled Sources hart stoppen (kurz nach dem Gain-Ramp).
    const stopAt = now + 0.04;
    for (const item of this.playbackQueue) {
      try {
        item.source.onended = null;
        item.source.stop(stopAt);
      } catch {
        // already stopped or not started — ignore
      }
    }
    this.playbackQueue = [];
    this.playbackHead = ctx.currentTime;

    // 3. Gain für die nächste Antwort zurücksetzen.
    setTimeout(() => {
      if (this.playbackGain && this.audioCtx) {
        const t = this.audioCtx.currentTime;
        try {
          this.playbackGain.gain.cancelScheduledValues(t);
          this.playbackGain.gain.setValueAtTime(1, t);
        } catch {
          // ignore
        }
      }
    }, 80);
  }

  private startLevelLoop(): void {
    const tick = () => {
      if (this.audioCtx && this.playbackAnalyser && this.analyserBuf) {
        this.playbackAnalyser.getFloatTimeDomainData(this.analyserBuf);
        let sum = 0;
        for (let i = 0; i < this.analyserBuf.length; i++)
          sum += this.analyserBuf[i] * this.analyserBuf[i];
        const rms = Math.sqrt(sum / this.analyserBuf.length);
        this.opts.handlers.onAssistantAudioLevel(rms);
      }
      // Mic-Level für UI: nur senden wenn nicht gerade JARVIS spricht (sonst
      // springt der Orb auf JARVIS' Echo). Hybrid-Barge-In nutzt das gleiche
      // Mic-RMS aber UNABHÄNGIG von dieser Bedingung — siehe checkBargeIn().
      let micRms = 0;
      if (this.micAnalyser && this.micAnalyserBuf) {
        this.micAnalyser.getFloatTimeDomainData(this.micAnalyserBuf);
        let sum = 0;
        for (let i = 0; i < this.micAnalyserBuf.length; i++)
          sum += this.micAnalyserBuf[i] * this.micAnalyserBuf[i];
        micRms = Math.sqrt(sum / this.micAnalyserBuf.length);
        if (this.state !== "speaking") {
          this.opts.handlers.onMicLevel(micRms);
        }
      }
      // Hybrid-Barge-In Check — nur während JARVIS spricht UND wir nicht im
      // Speaker-Mode-Gate sind (dort ist der Loop bewusst Half-Duplex).
      if (this.state === "speaking" && !this.speakerMode) {
        this.checkBargeIn(micRms);
      } else {
        this.bargeInFrameCounter = 0;
      }
      if (this.state === "closed" || this.state === "error") return;
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  /**
   * Hybrid-Barge-In: erkennt User-Stimme MITTEN in JARVIS' Antwort und cancelt
   * den Audio-Stream sofort — ohne auf Server-VAD warten zu müssen (das dauert
   * ~400-800ms und feelt "klebrig").
   *
   * Heuristik: Browser hat AEC aktiv. Echo aus dem Lautsprecher kommt am Mic
   * mit RMS ~0.02-0.08 an (Speaker-Mode höher, deshalb dort gated). Direktes
   * Sprechen erreicht 0.2-0.5. Schwelle 0.15 schneidet sauber dazwischen.
   * Plus: `bargeInMinFrames` damit ein einzelner Tap-Klick nicht reicht.
   */
  private checkBargeIn(micRms: number): void {
    if (!this.bargeInArmed) return;
    if (micRms < this.bargeInThreshold) {
      this.bargeInFrameCounter = 0;
      return;
    }
    this.bargeInFrameCounter += 1;
    if (this.bargeInFrameCounter < this.bargeInMinFrames) return;
    // Lock auslösen — armed wird im setState("listening") wieder freigegeben.
    this.bargeInArmed = false;
    this.bargeInFrameCounter = 0;
    // Hart cancel: Server-Side response.cancel + lokale Audio-Queue stoppen.
    // Aber: cancel NUR wenn echt eine response läuft (sonst "no active response").
    if (this.assistantSpeakingSince > 0 || this.state === "speaking" || this.state === "thinking") {
      try {
        this.sendEvent({ type: "response.cancel" });
      } catch {
        // ignore
      }
    }
    this.clearPlayback();
    this.setState("listening");
    this.opts.handlers.onUserSpeechStarted?.();
  }

  private setState(s: RealtimeState): void {
    if (this.state === s) return;
    this.state = s;
    // Speaker-Mode Half-Duplex: Gate folgt direkt dem State.
    //  speaking → Gate ZU (Mic-Frames nicht mehr an Server)
    //  listening/ready → Gate AUF (mit 300ms Tail-Delay nach speaking)
    if (this.speakerMode) {
      if (s === "speaking") {
        this.closeMicGate();
      } else if (s === "listening" || s === "ready" || s === "idle") {
        // Release mit Tail-Delay — damit Echo-Reverb nicht sofort wieder VAD triggert.
        this.scheduleMicGateRelease();
      } else if (s === "closed" || s === "error") {
        this.openMicGate();
      }
    }
    // Hybrid-Barge-In: Lock zurücksetzen sobald wir wieder hören. Damit
    // nicht ein noch-laufendes RMS-Tail direkt nochmal interruptet.
    if (s === "listening" || s === "ready" || s === "idle") {
      this.bargeInArmed = true;
      this.bargeInFrameCounter = 0;
    } else if (s === "speaking") {
      // Codex-Sparring: Warm-up 250ms (statt 150) — auf 30Hz-RAF (Low-Power-
      // Mobile) sind 3 Frames eigentlich ~100ms, nicht 50. Außerdem braucht
      // der erste Audio-Chunk ein bisschen Zeit bis er den Browser-Speaker
      // erreicht und im Mic als Echo ankommt. 250ms ist konservativ-sicher
      // und immer noch deutlich schneller als Server-VAD (~500-800ms).
      this.bargeInArmed = false;
      this.bargeInFrameCounter = 0;
      setTimeout(() => {
        if (this.state === "speaking") this.bargeInArmed = true;
      }, 250);
    }
    this.opts.handlers.onState(s);
  }

  private fail(message: string): void {
    this.opts.handlers.onError(message);
    this.setState("error");
    void this.stop();
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function describe(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = "";
  // Chunked, damit String.fromCharCode.apply nicht überläuft.
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + CHUNK)));
  }
  return btoa(binary);
}

function base64ToInt16(b64: string): Int16Array {
  const binary = atob(b64);
  const len = binary.length;
  const buf = new ArrayBuffer(len);
  const view = new Uint8Array(buf);
  for (let i = 0; i < len; i++) view[i] = binary.charCodeAt(i);
  return new Int16Array(buf);
}

function mergeTranscriptSegments(deltaText: string, doneText: string): string {
  const a = deltaText.trim();
  const b = doneText.trim();
  if (!a) return b;
  if (!b) return a;
  if (b.startsWith(a) || b.includes(a)) return b;
  if (a.startsWith(b) || a.includes(b)) return a;
  return `${a} ${b}`.trim();
}

// ── AudioWorklet-Source (inline, damit Vite den Bundle einfach hält) ─────────
//
// Resampler: einfache lineare Interpolation von ctx.sampleRate auf 24 kHz.
// PCM16-Output. Frames zu ~20 ms gebatcht und an den Main-Thread geschickt.
const WORKLET_SRC = `
class VeloroPcmEncoder extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const opts = options.processorOptions || {};
    this.targetRate = opts.targetSampleRate || 24000;
    this.frameSizeMs = opts.frameSizeMs || 20;
    this.frameSamples = Math.round(this.targetRate * (this.frameSizeMs / 1000));
    this.acc = [];
    this.accLen = 0;
    this.inputRate = sampleRate; // global from AudioWorkletGlobalScope
    this.ratio = this.inputRate / this.targetRate;
    this.resampleAcc = 0;
  }
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (!ch || ch.length === 0) return true;

    // Lineare Resampling-Schleife: für jedes Output-Sample interpolieren.
    const outLen = Math.floor((ch.length + this.resampleAcc) / this.ratio);
    if (outLen <= 0) {
      this.resampleAcc += ch.length;
      return true;
    }
    const out = new Float32Array(outLen);
    let inputPos = -this.resampleAcc;
    for (let i = 0; i < outLen; i++) {
      const srcIndex = inputPos + i * this.ratio;
      const i0 = Math.floor(srcIndex);
      const i1 = i0 + 1;
      const frac = srcIndex - i0;
      const s0 = i0 >= 0 && i0 < ch.length ? ch[i0] : 0;
      const s1 = i1 >= 0 && i1 < ch.length ? ch[i1] : 0;
      out[i] = s0 + (s1 - s0) * frac;
    }
    const consumed = (outLen * this.ratio) - this.resampleAcc;
    this.resampleAcc = ch.length - consumed;

    // RMS für Level-Anzeige
    let sum = 0;
    for (let i = 0; i < out.length; i++) sum += out[i] * out[i];
    const rms = Math.sqrt(sum / out.length);
    this.port.postMessage({ type: "level", rms });

    this.acc.push(out);
    this.accLen += out.length;
    while (this.accLen >= this.frameSamples) {
      const frame = new Float32Array(this.frameSamples);
      let off = 0;
      while (off < this.frameSamples && this.acc.length) {
        const head = this.acc[0];
        const need = this.frameSamples - off;
        if (head.length <= need) {
          frame.set(head, off);
          off += head.length;
          this.acc.shift();
        } else {
          frame.set(head.subarray(0, need), off);
          this.acc[0] = head.subarray(need);
          off = this.frameSamples;
        }
      }
      this.accLen -= this.frameSamples;
      // → PCM16
      const pcm = new Int16Array(frame.length);
      for (let i = 0; i < frame.length; i++) {
        const v = Math.max(-1, Math.min(1, frame[i]));
        pcm[i] = v < 0 ? v * 0x8000 : v * 0x7FFF;
      }
      this.port.postMessage({ type: "pcm", payload: pcm.buffer }, [pcm.buffer]);
    }
    return true;
  }
}
registerProcessor("veloro-pcm-encoder", VeloroPcmEncoder);
`;
